import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Retro DJ Theme - Warm analog vibes with orange energy
let theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#FF6B1A', // Warm vinyl orange
      light: '#FF8C4A',
      dark: '#CC5500',
      contrastText: '#000000',
    },
    secondary: {
      main: '#1A1A1A',
      light: '#2A2A2A',
      dark: '#0D0D0D',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0D0D0D', // Deep black like a vinyl record
      paper: '#151515',
    },
    error: { 
      main: '#FF4444', 
      light: '#FF6B6B',
      dark: '#CC0000',
    },
    warning: { 
      main: '#FFB800', 
    },
    info: { 
      main: '#4ECDC4', 
    },
    success: { 
      main: '#00D97E', 
    },
    text: {
      primary: '#F5F0E6', // Warm white like aged paper
      secondary: '#A09080', // Sepia-toned gray
    },
    divider: 'rgba(255, 107, 26, 0.15)',
  },
  typography: {
    // Retro-inspired font stack with character
    fontFamily: '"Space Mono", "JetBrains Mono", "IBM Plex Mono", monospace',
    h1: {
      fontFamily: '"Bebas Neue", "Impact", "Arial Black", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.05em',
      lineHeight: 0.95,
      textTransform: 'uppercase',
      fontSize: '3.5rem',
      '@media (min-width:600px)': {
        fontSize: '5rem',
      },
      '@media (min-width:900px)': {
        fontSize: '7rem',
      },
    },
    h2: {
      fontFamily: '"Bebas Neue", "Impact", "Arial Black", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.04em',
      lineHeight: 1,
      textTransform: 'uppercase',
      fontSize: '2rem',
      '@media (min-width:600px)': {
        fontSize: '2.75rem',
      },
      '@media (min-width:900px)': {
        fontSize: '3.5rem',
      },
    },
    h3: {
      fontFamily: '"Bebas Neue", "Impact", "Arial Black", sans-serif',
      fontWeight: 400,
      letterSpacing: '0.03em',
      lineHeight: 1.1,
      textTransform: 'uppercase',
      fontSize: '1.5rem',
      '@media (min-width:600px)': {
        fontSize: '1.75rem',
      },
      '@media (min-width:900px)': {
        fontSize: '2.25rem',
      },
    },
    h4: {
      fontFamily: '"Space Mono", monospace',
      fontWeight: 700,
      letterSpacing: '0.02em',
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: '"Space Mono", monospace',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: '"Space Mono", monospace',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.02em',
    },
    subtitle2: {
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    button: {
      fontFamily: '"Space Mono", monospace',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    overline: {
      fontFamily: '"Space Mono", monospace',
      fontWeight: 700,
      letterSpacing: '0.2em',
      fontSize: '0.7rem',
    },
  },
  shape: {
    borderRadius: 2, // Sharp, angular - like old equipment
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@import': [
          "url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Space+Mono:wght@400;700&display=swap')",
        ],
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          backgroundColor: '#0D0D0D',
          scrollbarColor: '#FF6B1A #0D0D0D',
          '&::-webkit-scrollbar': {
            width: 8,
            backgroundColor: '#0D0D0D',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 0,
            backgroundColor: '#FF6B1A',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#FF8C4A',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontWeight: 700,
          padding: '12px 28px',
          transition: 'all 0.15s ease',
          boxShadow: 'none',
          position: 'relative',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          background: '#FF6B1A',
          color: '#000000',
          border: '2px solid #FF6B1A',
          '&:hover': {
            background: '#000000',
            color: '#FF6B1A',
            boxShadow: 'inset 0 0 20px rgba(255, 107, 26, 0.3)',
          },
        },
        outlinedPrimary: {
          borderColor: '#FF6B1A',
          borderWidth: '2px',
          color: '#FF6B1A',
          '&:hover': {
            borderWidth: '2px',
            borderColor: '#FF6B1A',
            background: 'rgba(255, 107, 26, 0.1)',
          },
        },
        text: {
          color: '#F5F0E6',
          '&:hover': {
            background: 'rgba(255, 107, 26, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            backgroundColor: '#1A1A1A',
            fontFamily: '"Space Mono", monospace',
            '& fieldset': {
              borderColor: '#333333',
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: '#555555',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FF6B1A',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"Space Mono", monospace',
            color: '#666666',
            letterSpacing: '0.05em',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#FF6B1A',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#151515',
          borderRadius: 0,
          border: '1px solid #2A2A2A',
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#151515',
          borderRadius: 0,
          border: '2px solid #2A2A2A',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#FF6B1A',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0D0D0D',
          borderRight: '2px solid #FF6B1A',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#2A2A2A',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: '#1A1A1A',
          color: '#F5F0E6',
          fontFamily: '"Space Mono", monospace',
          letterSpacing: '0.05em',
        },
        outlined: {
          borderColor: '#FF6B1A',
          borderWidth: '2px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: '2px solid',
          fontFamily: '"Space Mono", monospace',
        },
        standardError: {
          backgroundColor: 'rgba(255, 68, 68, 0.1)',
          borderColor: '#FF4444',
        },
        standardSuccess: {
          backgroundColor: 'rgba(0, 217, 126, 0.1)',
          borderColor: '#00D97E',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 184, 0, 0.1)',
          borderColor: '#FFB800',
        },
        standardInfo: {
          backgroundColor: 'rgba(78, 205, 196, 0.1)',
          borderColor: '#4ECDC4',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#151515',
          border: '2px solid #FF6B1A',
          borderRadius: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Space Mono", monospace',
          '&:hover': {
            backgroundColor: 'rgba(255, 107, 26, 0.15)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 107, 26, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 107, 26, 0.25)',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#FF6B1A',
          minWidth: 40,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#F5F0E6',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: 'rgba(255, 107, 26, 0.15)',
            color: '#FF6B1A',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#555555',
          '&.Mui-checked': {
            color: '#FF6B1A',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#555555',
          borderRadius: 0,
          '&.Mui-checked': {
            color: '#FF6B1A',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#FF6B1A',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#FF6B1A',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '0 20px',
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            minHeight: '56px',
            padding: '0 12px',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:600px)': {
            margin: 16,
            maxHeight: 'calc(100% - 32px)',
            maxWidth: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '12px 8px',
          },
        },
      },
    },
  },
});

// Apply responsive font sizes
theme = responsiveFontSizes(theme);

export default theme;
